import type { ActionFunctionArgs } from '@remix-run/node';
import { ServerErrorResponse } from '@modules/root/graphql/responses';
import type {
    CreateOrUpdateInspirationMutation,
    CreateOrUpdateInspirationMutationVariables,
} from 'generated/graphql';
import { CreateOrUpdateInspirationDocument } from 'generated/graphql';
import { assertResponse } from '@modules-rocco/root/graphql/assertResponse.server';

export const ACTION_CREATE_MOOD_BOARD = '/actions/create-mood-board';

interface CreateMoodBoardFormData {
    title: string;
    description: string;
    isPrivate: string;
    buildHubId: string;
}

/**
 * Custom action that mimics ACTION_CREATE_DESIGN_BOARD but uses the
 * CreateOrUpdateInspiration mutation and returns a DesignBoardViewModel shape.
 */
export const action = async ({ request, context }: ActionFunctionArgs) => {
    const formData = await request.formData();
    const formObj = Object.fromEntries(
        formData
    ) as unknown as CreateMoodBoardFormData;

    if (!formObj.title) {
        return new ServerErrorResponse('Title is required');
    }
    if (!formObj.buildHubId) {
        return new ServerErrorResponse('BuildHub ID is required');
    }

    const variables: CreateOrUpdateInspirationMutationVariables = {
        Title: formObj.title,
        Description: formObj.description,
        Private: ['true', '1'].includes(formObj.isPrivate),
        BuildHubID: formObj.buildHubId,
    };

    try {
        const { data: result, errors } = await context.client.mutate<
            CreateOrUpdateInspirationMutation,
            CreateOrUpdateInspirationMutationVariables
        >({
            mutation: CreateOrUpdateInspirationDocument,
            variables,
        });

        if (errors?.length) {
            throw new Response(
                JSON.stringify({
                    error: true,
                    message:
                        errors[0]?.message || 'Failed to create mood board',
                }),
                {
                    status: 500,
                    headers: { 'Content-Type': 'application/json' },
                }
            );
        }

        const newBoard = assertResponse(result?.projectInspirationGroupUpsert);

        return {
            id: newBoard.ID,
            title: newBoard.Title,
            urlSegment: newBoard.URLSegment,
            isNew: true,
        };
    } catch (error) {
        // If it's already a Response object, re-throw it
        if (error instanceof Response) {
            throw error;
        }

        throw new Response(
            JSON.stringify({
                error: true,
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to create mood board',
            }),
            {
                status: 500,
                headers: { 'Content-Type': 'application/json' },
            }
        );
    }
};
